import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { PageHeader } from '@/layouts/AppLayout';
import { formatCurrency, formatDateOnly } from '@/lib/utils';
import { cn } from '@/lib/utils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface RateRow {
  id: string;
  hotelRoomType: { id: string; name: string };
  mealPlan: string;
  occupancy: string;
  rate: string;
  currency: string;
  validFrom: string;
  validTo: string;
  notes: string | null;
}

interface MatrixEntry {
  hotel: { id: string; name: string; city: string };
  clients: Array<{
    client: { id: string; name: string; type: string };
    rates: RateRow[];
  }>;
}

// ── Derived matrix types ──────────────────────────────────────────────────────

interface HotelCol {
  id: string;
  name: string;
  city: string;
}

interface ClientRow {
  id: string;
  name: string;
}

// ── Drawer ────────────────────────────────────────────────────────────────────

const OCCUPANCY_LABEL: Record<string, string> = {
  single: 'Single',
  double: 'Double',
  triple: 'Triple',
  child: 'Child',
};

function RatesDrawer({
  open,
  onClose,
  hotel,
  client,
  rates,
}: {
  open: boolean;
  onClose: () => void;
  hotel: HotelCol | null;
  client: ClientRow | null;
  rates: RateRow[];
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content className="fixed right-0 top-0 h-full w-[520px] max-w-full bg-white shadow-xl z-50 flex flex-col">
          <div className="flex items-start justify-between px-5 py-4 border-b border-neutral-200">
            <div>
              <Dialog.Title className="text-base font-semibold text-neutral-900">
                {hotel?.name ?? '—'}
              </Dialog.Title>
              <Dialog.Description className="text-sm text-neutral-500 mt-0.5">
                {client?.name ?? '—'} · {rates.length} rate{rates.length !== 1 ? 's' : ''}
              </Dialog.Description>
            </div>
            <div className="flex items-center gap-2">
              <Button asChild size="sm">
                <Link
                  to={`/rates/new?hotelId=${hotel?.id ?? ''}&clientId=${client?.id ?? ''}`}
                >
                  Add rate
                </Link>
              </Button>
              <Dialog.Close asChild>
                <button className="ml-2 text-neutral-400 hover:text-neutral-700 text-xl leading-none" aria-label="Close">
                  ×
                </button>
              </Dialog.Close>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {rates.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-neutral-400">
                No rates for this pair yet.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium text-neutral-500">Room</th>
                    <th className="px-4 py-2.5 text-left font-medium text-neutral-500">Plan</th>
                    <th className="px-4 py-2.5 text-left font-medium text-neutral-500">Occ.</th>
                    <th className="px-4 py-2.5 text-right font-medium text-neutral-500">Rate/night</th>
                    <th className="px-4 py-2.5 text-left font-medium text-neutral-500">Valid</th>
                    <th className="px-4 py-2.5 text-right font-medium text-neutral-500"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {rates.map((r) => (
                    <tr key={r.id} className="hover:bg-neutral-50">
                      <td className="px-4 py-2.5 text-neutral-800">{r.hotelRoomType?.name}</td>
                      <td className="px-4 py-2.5 text-neutral-600">{r.mealPlan.toUpperCase()}</td>
                      <td className="px-4 py-2.5 text-neutral-600 capitalize">
                        {OCCUPANCY_LABEL[r.occupancy] ?? r.occupancy}
                      </td>
                      <td className="px-4 py-2.5 text-right font-medium text-neutral-900">
                        {formatCurrency(r.rate)}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-neutral-500">
                        {formatDateOnly(r.validFrom)} – {formatDateOnly(r.validTo)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Link
                          to={`/rates/${r.id}/edit`}
                          className="text-xs text-neutral-400 hover:text-tangerine-600"
                        >
                          Edit
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ── Matrix cell ───────────────────────────────────────────────────────────────

function MatrixCell({
  count,
  onClick,
}: {
  count: number | undefined;
  onClick: () => void;
}) {
  if (!count) {
    return (
      <td className="px-3 py-3 text-center border-r border-neutral-100 last:border-0">
        <span className="inline-block w-2 h-2 rounded-full bg-danger-700 opacity-60" title="No contract" />
      </td>
    );
  }

  return (
    <td className="px-3 py-3 text-center border-r border-neutral-100 last:border-0">
      <button
        onClick={onClick}
        className={cn(
          'inline-flex items-center justify-center min-w-[2rem] rounded-sm px-2 py-0.5',
          'text-xs font-medium bg-tangerine-50 text-tangerine-700 hover:bg-tangerine-100 transition-colors',
        )}
      >
        {count}
      </button>
    </td>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function RatesListPage() {
  const [drawer, setDrawer] = useState<{
    hotel: HotelCol;
    client: ClientRow;
    rates: RateRow[];
  } | null>(null);

  const { data: matrix = [], isLoading } = useQuery({
    queryKey: ['contracted-rates', 'matrix'],
    queryFn: () => api.get<MatrixEntry[]>('/contracted-rates/matrix'),
  });

  // Derive ordered column list (hotels) and row list (clients) from matrix data
  const hotelMap = new Map<string, HotelCol>();
  const clientMap = new Map<string, ClientRow>();
  // cellMap[clientId][hotelId] = rates
  const cellMap = new Map<string, Map<string, RateRow[]>>();

  for (const entry of matrix) {
    const hotel = entry.hotel;
    if (!hotelMap.has(hotel.id)) hotelMap.set(hotel.id, hotel);

    for (const { client, rates } of entry.clients) {
      if (!clientMap.has(client.id)) clientMap.set(client.id, { id: client.id, name: client.name });

      if (!cellMap.has(client.id)) cellMap.set(client.id, new Map());
      cellMap.get(client.id)!.set(hotel.id, rates);
    }
  }

  const hotels = [...hotelMap.values()];
  const clients = [...clientMap.values()].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div>
      <PageHeader
        title="Contracted Rates"
        description="Matrix of rates by client (rows) and hotel (columns)"
        action={
          <Button asChild>
            <Link to="/rates/new">Add rate</Link>
          </Button>
        }
      />

      <div className="p-6">
        {isLoading && (
          <div className="flex justify-center py-16">
            <Spinner className="h-6 w-6" />
          </div>
        )}

        {!isLoading && hotels.length === 0 && (
          <div className="rounded-lg border border-dashed border-neutral-300 py-16 text-center text-sm text-neutral-400">
            No contracted rates yet.{' '}
            <Link to="/rates/new" className="text-tangerine-600 hover:underline">
              Add the first one
            </Link>
          </div>
        )}

        {!isLoading && hotels.length > 0 && (
          <div className="rounded-lg border border-neutral-200 bg-white overflow-x-auto">
            <table className="min-w-full text-sm border-collapse">
              <thead>
                <tr className="bg-neutral-50 border-b border-neutral-200">
                  {/* Top-left corner cell */}
                  <th className="sticky left-0 z-10 bg-neutral-50 px-4 py-3 text-left font-medium text-neutral-500 border-r border-neutral-200 min-w-[180px]">
                    Client
                  </th>
                  {hotels.map((hotel) => (
                    <th
                      key={hotel.id}
                      className="px-3 py-3 text-center font-medium text-neutral-500 border-r border-neutral-100 last:border-0 min-w-[120px]"
                    >
                      <Link
                        to={`/hotels/${hotel.id}`}
                        className="hover:text-tangerine-600 leading-tight block"
                      >
                        {hotel.name}
                        <span className="block text-xs font-normal text-neutral-400">{hotel.city}</span>
                      </Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {clients.map((client) => {
                  const clientCells = cellMap.get(client.id);
                  return (
                    <tr key={client.id} className="hover:bg-neutral-50 transition-colors">
                      <td className="sticky left-0 z-10 bg-white px-4 py-3 font-medium text-neutral-800 border-r border-neutral-200">
                        <Link
                          to={`/clients/${client.id}`}
                          className="hover:text-tangerine-600"
                        >
                          {client.name}
                        </Link>
                      </td>
                      {hotels.map((hotel) => {
                        const rates = clientCells?.get(hotel.id) ?? [];
                        return (
                          <MatrixCell
                            key={hotel.id}
                            count={rates.length || undefined}
                            onClick={() =>
                              setDrawer({ hotel, client, rates })
                            }
                          />
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <RatesDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        hotel={drawer?.hotel ?? null}
        client={drawer?.client ?? null}
        rates={drawer?.rates ?? []}
      />
    </div>
  );
}
